import type { Tone } from '../ui/Badge';

/**
 * The register's words (107) — design-language §6.2 one noun over: exactly one plain
 * vocabulary per thing, defined once, never a raw DB value on screen.
 *
 * Statuses are NOT here: `ASSET_STATUS` lives in `../status.ts` with every other entity's,
 * because that file is the law and a second status map next to it is how vocabularies
 * drift. These two are the register's non-status vocabularies, and the due chip below is
 * the copy rule that goes with them.
 */
export const ASSET_KIND_LABEL: Record<string, string> = {
  vehicle: 'Vehicle',
  tool: 'Tool',
};

export const MAINTENANCE_TYPE_LABEL: Record<string, string> = {
  service: 'Service',
  insurance: 'Insurance',
  registration: 'Registration',
  inspection: 'Inspection',
  // 'custom' is the shape, not the word — nobody schedules an "custom" on a van.
  custom: 'Other',
};

/** A hand-labelled entry says what it is; the rest are named by their type. */
export function dueTitle(row: { type: string; label?: string | null }): string {
  return row.label?.trim() || MAINTENANCE_TYPE_LABEL[row.type] || row.type;
}

/** Anything owed inside a fortnight is worth looking at before it is late. */
export const DUE_SOON_DAYS = 14;

/**
 * How a deadline reads, and in what tone — from the day count the SERVER already worked
 * out against the SHOP's clock.
 *
 * ⭐ Nothing in here constructs a Date, and that is the whole point of the function
 * existing. Doing this subtraction in the browser is what lets a laptop open in Denver
 * move a Boston shop's registration deadline by a day, and 107's acceptance says the
 * arithmetic belongs in SQL on the org clock. This turns one integer into words.
 *
 * One function rather than a label helper and a tone helper, because the two must agree:
 * a chip reading "Due today" in red, or "3 days overdue" in grey, is worse than either
 * mistake alone. `doneAt` is checked FIRST — a completed entry is history, and history is
 * never overdue no matter how late it was done.
 */
export function dueChip(row: {
  daysUntil: number;
  overdue: boolean;
  doneAt?: Date | string | null;
}): { label: string; tone: Tone } {
  if (row.doneAt) return { label: 'Done', tone: 'success' };
  const d = row.daysUntil;
  if (row.overdue) {
    return { label: d === -1 ? '1 day overdue' : `${-d} days overdue`, tone: 'danger' };
  }
  // Not overdue and not done, yet the date has passed: the server said so, and the server
  // is the one holding the clock. Say the days plainly rather than inventing a verdict.
  if (d < 0) return { label: `${-d} days ago`, tone: 'neutral' };
  if (d === 0) return { label: 'Due today', tone: 'warn' };
  if (d === 1) return { label: 'Due tomorrow', tone: 'warn' };
  return { label: `In ${d} days`, tone: d <= DUE_SOON_DAYS ? 'warn' : 'neutral' };
}
